/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';

import { SectionName } from '../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../common/analytics/useSectionReveal';
import { selectMetrics } from '../../../../utils/orgMetrics';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ConnectedAppsHighlightSection } from '../../../components/Sections/ConnectedAppsHighlightSection';
import { GraphGrowthSection } from '../../../components/Sections/GraphGrowthSection';
import { UserHighlightSection } from '../../../components/Sections/UserHighlightSection';
import { OrgMetricsAtom } from '../../../state';

const styles = cssMap({
	revealAnchor: {
		width: '100%',
	},
	loadingSlot: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '60vh',
	},
});

const OrgMetricsLoadable = loadable(OrgMetricsAtom);

export const OrgMetricsSection = (): JSX.Element => {
	const loadableState = useAtomValue(OrgMetricsLoadable);
	const sectionRef = useSectionReveal<HTMLDivElement>(SectionName.OrgMetrics);

	if (loadableState.state === 'loading') {
		return (
			<div ref={sectionRef} css={styles.loadingSlot}>
				<LoadingSpinner size="large" />
			</div>
		);
	}

	const metrics = loadableState.state === 'hasData' ? loadableState.data.data : null;
	const error = loadableState.state === 'hasData' ? loadableState.data.error : null;

	if (error) {
		// eslint-disable-next-line no-console
		console.warn('OrgMetricsSection: falling back to placeholder stats —', error);
	}

	const { sections } = selectMetrics(metrics);

	return (
		<div ref={sectionRef} css={styles.revealAnchor}>
			<UserHighlightSection stats={sections.intro} />
			<GraphGrowthSection stats={sections.graphShape} />
			<ConnectedAppsHighlightSection stats={sections.graphConnectivity} />
		</div>
	);
};
